import React, { useRef, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretLeft, faCaretRight } from "@fortawesome/free-solid-svg-icons";

const NextArrow = ({ onClick }) => (
  <div className="slick-custom-arrow slick-next" onClick={onClick}>
    <FontAwesomeIcon icon={faCaretRight} />
  </div>
);

const PrevArrow = ({ onClick }) => (
  <div className="slick-custom-arrow slick-prev" onClick={onClick}>
    <FontAwesomeIcon icon={faCaretLeft} />
  </div>
);

const Section4 = ({ url }) => {
  const sectionRef = useRef(null);
  const [Slick, setSlick] = useState(null);
  const slickLoaded = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      async (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !slickLoaded.current) {
          slickLoaded.current = true;
          const slickModule = await import("react-slick");
          await import("slick-carousel/slick/slick.css");
          await import("slick-carousel/slick/slick-theme.css");
          setSlick(() => slickModule.default);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const settings = {
    dots: true,
    arrows: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    pauseOnHover: true,
    speed: 600,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
  };

const cases = [
  {
    image: `${url}images/case-studies/mairotec.png`,
    title: "Custom HMI Platform for Industrial Robot",
    tags: ["Industrial Automation", "Enterprise", "Global"],
    client: "MairoTec",
    industry:
      "Industrial Automation / Robotics / Manufacturing | Enterprise, multi-sector deployment",
    problem:
      "Fragmented control interfaces and limited real-time robot visibility forced operators to work across multiple tools with manual, error-prone workflows, creating safety risks and operational inefficiency at scale.",
    built:
      "A web-based, PLC-integrated Human-Machine Interface deployed on ctrlX Core, combining ReactJS, Node.js, real-time WebSocket communication, OPC UA industrial protocol, interactive 3D visualization, and role-based access control into a single centralized robotic operations platform.",
    result:
      "Fragmented HMIs replaced with a unified control layer, continuous real-time robot visibility, standardized safety protocols, reduced manual control errors, and a vendor-independent OPC UA architecture ready for Industry 4.0 scale.",
    quote:
      "ExD helped us replace fragmented HMIs with a unified, scalable control layer. The result is improved operational visibility, safer system interaction, and a future-ready foundation for deploying advanced robotics across sectors.",
    quoteAuthor: "Andreas Mathes, CEO @ MairoTec",
  },
  {
    image: `${url}images/case-studies/evGo.png`,
    title: "Custom Financial Intelligence Platform",
    tags: ["Energy", "Enterprise", "USA"],
    client: "EVgo",
    industry:
      "EV Charging Network / Energy Infrastructure | US Enterprise (1,100+ stations across 40 states)",
    problem:
      "Spreadsheet-driven financial operations could not scale to validate electricity bills, model profitability, or plan capital investments reliably across a rapidly expanding nationwide charging network.",
    built:
      "A custom AI-enabled web application suite including a Bill Check Engine for automated bill validation via third-party APIs, the Bertie Financial Intelligence Platform replacing Excel-based models, and a CAPEX Estimator Module built on Python, Django, and predictive analytics.",
    result:
      "End-to-end automation of electricity bill validation, elimination of manual reconciliation, consistent financial visibility across sites, and data-driven CAPEX and OPEX planning for expansion.",
    quote:
      "ExD helped us replace manual, spreadsheet-driven processes with a more structured financial intelligence platform, enabling clearer insight, better forecasting, and more confident decision-making as we expand.",
    quoteAuthor: "Badar Khan, CEO @ EVgo",
  },
  {
    image: `${url}images/case-studies/embark.png`,
    title: "AI-Powered Sports Coaching Platform",
    tags: ["Sports Tech", "Startup", "North America"],
    client: "Embark Athletics",
    industry:
      "Sports Technology / Youth Sports Coaching | Growth-stage, Canada and USA",
    problem:
      "Coaches relied on manual, static drill libraries and training plans that could not adapt to individual player needs or scale consistently across growing youth programs.",
    built:
      "An AI-powered coaching platform with intelligent drill generation, dynamic game-phase modeling, and personalized training plans, built using Python, Django, PostgreSQL, and integrated AI models including OpenAI, Claude, and Gemini.",
    result:
      "Shifted coaching from manual planning to data-driven training, enabling instant drill generation, personalized sessions, and scalable coaching frameworks without increasing workload.",
    quote:
      "ExD worked closely with us to design and build an AI-powered coaching platform that brings structure, adaptability, and intelligence into the training process.",
    quoteAuthor: "CEO @ Embark Athletics",
  },
];

  return (
    <section className="home-section-4" ref={sectionRef}>
      <div className="container overflow-hidden">
        <h2 className="s4-heading" data-aos="fade-up">CASE STUDIES</h2>

        <div className="s4-slider-wrapper" data-aos="fade-up" data-aos-delay="150">
          {Slick ? (
            <Slick {...settings}>
              {cases.map((c, idx) => (
                <div key={idx}>
                  <div className="s4-card">

                    {/* LEFT — image */}
                    <div className="s4-card-image">
                      <img src={c.image} alt={c.title} />
                    </div>

                    {/* RIGHT — content */}
                    <div className="s4-card-content">
                      <h3 className="s4-card-title">{c.title}</h3>

                      <div className="s4-tags">
                        {c.tags.map((tag, i) => (
                          <React.Fragment key={i}>
                            <span className="s4-tag">{tag}</span>
                            {i < c.tags.length - 1 && <span className="s4-tag-sep">|</span>}
                          </React.Fragment>
                        ))}
                      </div>

                      <p className="s4-meta"><strong>Client:</strong> {c.client}</p>
                      <p className="s4-meta"><strong>Industry & Size:</strong> {c.industry}</p>
                      <p className="s4-meta"><strong>The Problem:</strong> {c.problem}</p>
                      <p className="s4-meta"><strong>What We Built:</strong> {c.built}</p>
                      <p className="s4-meta"><strong>The Result:</strong> {c.result}</p>

                      <div className="s4-quote-box">
                        <div className="s4-quote-avatar" />
                        <div>
                          <p className="s4-quote-text">"{c.quote}"</p>
                          <p className="s4-quote-author">{c.quoteAuthor}</p>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              ))}
            </Slick>
          ) : (
            <div className="s4-skeleton" />
          )}
        </div>
      </div>
    </section>
  );
};

export default Section4;